import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import AnalyticsAPI from '../APIs/analyticsAPI';
import API from '../context/API';
import { Category, CategorySalesApiResponse, KeyMetricsFilter } from '../components/Analytics/CategorySales/constants';

// Request parameters
export interface CategorySalesRequestParams {
  startDate?: string;
  endDate?: string;
  filter?: string;
  merchantId?: string;
  store?: string;
}

interface CategorySalesState {
  categories: Category[];
  keyMetrics: KeyMetricsFilter;
  loading: boolean;
  error: string | null;
  weeklyChange: number;
}

const initialState: CategorySalesState = {
  categories: [],
  keyMetrics: {
    totalRevenue: "0.00",
    averageRevenue: "0.00",
    bestCategoryByRevenue: "0.00",
    bestCategoryByUnitsSold: 0,
  },
  loading: false,
  error: null,
  weeklyChange: 0,
};

// Async thunk to fetch category sales data
export const fetchCategorySales = createAsyncThunk<
  { categories: Category[]; keyMetrics: KeyMetricsFilter },
  CategorySalesRequestParams,
  { rejectValue: string }
>('categorySales/fetchCategorySales', async (params, { rejectWithValue }) => {
  try {
    const options: any = {};
    
    if (params.startDate && params.endDate) {
      options.startDate = params.startDate;
      options.endDate = params.endDate;
    }
    
    if (params.filter) {
      options.filter = params.filter;
    }
    
    if (params.merchantId) {
      options.merchantId = params.merchantId;
    }
    
    if (params.store) {
      options.store = params.store;
    }

    const response = await AnalyticsAPI.getCategorySales(options);
    const categoriesData: Category[] = response?.data?.categories || response?.categories || [];
    
    // Calculate key metrics
    const totalRevenue = categoriesData.length > 0
      ? categoriesData.reduce(
          (sum, category) => sum + (parseFloat(String(category.totalRevenue || 0)) || 0),
          0
        )
      : 0;
    
    const averageRevenue = categoriesData.length > 0 ? totalRevenue / categoriesData.length : 0;

    // Find best category by revenue
    const bestCategoryByRevenue = categoriesData.length > 0
      ? categoriesData.reduce((prev, current) =>
          parseFloat(String(current.totalRevenue || 0)) >
          parseFloat(String(prev.totalRevenue || 0))
            ? current
            : prev
        )
      : { category: "No Data", totalRevenue: 0 };

    // Find best category by units sold
    const bestCategoryByUnitsSold = categoriesData.length > 0
      ? categoriesData.reduce((prev, current) =>
          (current.totalItemsSold || 0) > (prev.totalItemsSold || 0)
            ? current
            : prev
        )
      : { category: "No Data", totalItemsSold: 0 };

    const keyMetrics: KeyMetricsFilter = {
      totalRevenue: totalRevenue.toFixed(2),
      averageRevenue: averageRevenue.toFixed(2),
      bestCategoryByRevenue: parseFloat(
        String(bestCategoryByRevenue.totalRevenue || 0)
      ).toFixed(2),
      bestCategoryByUnitsSold: bestCategoryByUnitsSold.totalItemsSold || 0,
    };

    return {
      categories: categoriesData,
      keyMetrics,
    };
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch category sales data'
    );
  }
});

// Async thunk to fetch stores
export const fetchStores = createAsyncThunk<
  { id: string; name?: string }[],
  void,
  { rejectValue: string }
>('categorySales/fetchStores', async (_, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await API.get('/stores', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const stores = response.data.data || [];
    return stores;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch stores'
    );
  }
});

const categorySalesSlice = createSlice({
  name: 'categorySales',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCategorySales: (state) => {
      state.categories = [];
      state.keyMetrics = initialState.keyMetrics;
    },
    setWeeklyChange: (state, action) => {
      state.weeklyChange = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch category sales data
      .addCase(fetchCategorySales.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategorySales.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload.categories;
        state.keyMetrics = action.payload.keyMetrics;
      })
      .addCase(fetchCategorySales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.categories = [];
        state.keyMetrics = initialState.keyMetrics;
      });
  },
});

export const { clearError, clearCategorySales, setWeeklyChange } = categorySalesSlice.actions;
export default categorySalesSlice.reducer;
